import { observer } from 'mobx-react';
import { Component, ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';

import App from './App';
import { ConfiguratorState } from './configurator/ConfiguratorState';
import { MetadataProvider } from './context/MetadataProvider';
import { PriceProvider } from './context/PriceContext';
import { ShoppingProvider } from './context/ShoppingContext';
import { InitialStoreSelectorModal } from './layout/InitialStoreSelectorModal/InitialStoreSelectorModal';
import { LayoutTranslation } from './localization/SiteTranslation';
import ModularConfiguratorPageState from './modularConfigurator/ModularConfiguratorPageState';
import { BasePageState } from './shared/BasePageState';
import { IPageState } from './shared/common';
import ScrollToTopButton from './shared/components/Button/ScrollToTopButton';
import FullPageLoader from './shared/components/Loaders/FullPageLoader';
import NoConnectionModal from './shared/components/NoConnectionModal/NoConnectionModal';
import RedirectionWarningModal from './shared/components/RedirectionWarningModal/RedirectionWarningModal';
import { RegionContext } from './shared/regions/RegionContext';

interface Props {
  app: App;
}

export interface IContainerContentStyle {
  visibility: 'hidden' | 'visible';
  position: 'static' | 'fixed';
  width: string;
  height: string;
  top: number;
  left: number;
}

@observer
class Shell extends Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  renderMetaTags(currentPage: IPageState, translation: LayoutTranslation) {
    if (!(currentPage instanceof BasePageState)) {
      throw `Each page state has to implement ${BasePageState.name}.`;
    }
    const { titleFormat } = translation;

    const basePageState = currentPage as BasePageState<{}>;
    const metadata = basePageState.metadata;
    const titlePerPage = metadata.title ? metadata.title : 'Flokk';

    const formattedTitle = titleFormat.interpolate([['title', titlePerPage]]);
    const metaTags: { names: string[]; value: string }[] = [
      {
        names: ['og:type'],
        value: 'article',
      },
      {
        names: ['og:title'],
        value: metadata.title,
      },
      {
        names: ['og:description', 'description'],
        value: metadata.description,
      },
      {
        names: ['url', 'og:url'],
        value: metadata.url,
      },
      {
        names: ['image', 'og:image', 'og:image:secure_url'],
        value: metadata.imageUrl,
      },
      {
        names: ['og:image:width'],
        value: metadata.imageWidth,
      },
      {
        names: ['og:image:height'],
        value: metadata.imageHeight,
      },
    ];

    const metaData: ReactNode[] = metaTags.mapMany((x) =>
      x.value != null ? x.names.map((name) => <meta property={name} key={name} content={x.value} />) : [],
    );

    return (
      <Helmet prioritizeSeoTags>
        <title>{formattedTitle}</title>
        {metaData}
      </Helmet>
    );
  }

  render() {
    const { appState, metadataContext, layout, regionManager } = this.props.app;

    const { store } = appState;

    const isPricesEnable = store?.canShowPrices();
    const shoppingContext = store?.shoppingContext;
    const shouldDisplayDecimalPartOfPrices = store?.shouldDisplayDecimalPartOfPrices ?? true;

    const isConfiguratorPageView =
      appState.currentPage instanceof ConfiguratorState || appState.currentPage instanceof ModularConfiguratorPageState;

    const contentContainerStyle: IContainerContentStyle = {
      visibility: appState.loadingPageIndicator.isLoading ? 'hidden' : 'visible',
      position: appState.loadingPageIndicator.isLoading ? 'fixed' : isConfiguratorPageView ? 'fixed' : 'static',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    };

    return (
      <MetadataProvider context={metadataContext}>
        <RegionContext.Provider value={{ regionManager }}>
          <ShoppingProvider shoppingContext={shoppingContext}>
            <PriceProvider enable={isPricesEnable} displayDecimalPart={shouldDisplayDecimalPartOfPrices}>
              {appState.loadingPageIndicator.isLoading ? <FullPageLoader /> : null}
              <div id="root" style={contentContainerStyle}>
                {layout}
              </div>
              <ScrollToTopButton translation={appState.translation.scrollToTop} shoppingContext={shoppingContext} />
              <NoConnectionModal
                modal={appState.noConnectionModal}
                translation={appState.translation.noConnectionModal}
              />
              <InitialStoreSelectorModal
                modal={appState.initialStoreSelectionModal}
                translation={appState.translation.initialStoreSelectorModal}
                image={appState.navigationState.initialStoreSelector?.backgroundImage}
                videoUrl={appState.navigationState.initialStoreSelector?.backgroundVideoUrl}
              />
              <RedirectionWarningModal
                modal={appState.redirectionWarningModalState}
                translation={appState.translation.redirectionWarningModal}
              />
            </PriceProvider>
            {this.renderMetaTags(appState.currentPage, appState.translation.layout)}
          </ShoppingProvider>
        </RegionContext.Provider>
      </MetadataProvider>
    );
  }
}

export default Shell;
